import type React from "react";
import {
	ResizableHandle,
	ResizablePanel,
	ResizablePanelGroup,
} from "@/components/ui/resizable";
import { ScrollArea } from "@/components/ui/scroll-area";
import type { FormSchema } from "@/lib/database";
import { BlockManager } from "../../block-manager";
import { FieldPalette } from "../../field-palette";
import { FieldSettingsPanel } from "../../field-settings-panel";
import { FormPreview } from "../../form-preview";

import { PANEL_SIZES } from "../constants";

import type { FormBuilderPanelsProps } from "../types";
import { getAllFields } from "../utils";

export const FormBuilderPanels: React.FC<FormBuilderPanelsProps> = ({
	formSchema,
	selectedFieldId,
	selectedBlockId,
	selectedField,
	onFieldAdd,
	onFieldSelect,
	onFieldUpdate,
	onFieldDelete,
	onFieldsReorder,
	onBlockSelect,
	onBlocksUpdate,
	onBlockAdd,
	onBlockUpdate,
	onBlockDelete,
	onFormSettingsUpdate,
	onStepSelect,
}) => {
	const allFields = getAllFields(formSchema);

	const handleSchemaUpdate = (updatedSchema: FormSchema) => {
		if (updatedSchema.fields && updatedSchema.fields !== formSchema.fields) {
			onFieldsReorder(updatedSchema.fields);
		}

		if (updatedSchema.blocks && updatedSchema.blocks !== formSchema.blocks) {
			onBlocksUpdate(updatedSchema.blocks);
		}

		if (
			updatedSchema.settings &&
			updatedSchema.settings !== formSchema.settings
		) {
			onFormSettingsUpdate(updatedSchema.settings);
		}
	};
	return (
		<ResizablePanelGroup
			className="h-full"
			orientation="horizontal"
			style={{
				overscrollBehavior: "contain",
			}}
		>
			{}
			<ResizablePanel
				defaultSize={PANEL_SIZES.LEFT_PANEL.default}
				maxSize={PANEL_SIZES.LEFT_PANEL.max}
				minSize={PANEL_SIZES.LEFT_PANEL.min}
				style={{
					overscrollBehavior: "contain",
				}}
			>
				{formSchema.settings.multiStep ? (
					<BlockManager
						blocks={formSchema.blocks}
						onBlockAdd={onBlockAdd}
						onBlockDelete={onBlockDelete}
						onBlockSelect={onBlockSelect}
						onBlocksUpdate={onBlocksUpdate}
						onFieldDelete={onFieldDelete}
						onFieldSelect={onFieldSelect}
						selectedBlockId={selectedBlockId}
						selectedFieldId={selectedFieldId}
					/>
				) : (
					<FieldPalette
						formSchema={formSchema}
						onAddField={onFieldAdd}
						onSchemaUpdate={handleSchemaUpdate}
					/>
				)}
			</ResizablePanel>

			<ResizableHandle />

			{}
			<ResizablePanel
				defaultSize={PANEL_SIZES.PREVIEW_PANEL.default}
				maxSize={PANEL_SIZES.PREVIEW_PANEL.max}
				minSize={PANEL_SIZES.PREVIEW_PANEL.min}
				style={{
					overscrollBehavior: "contain",
				}}
			>
				<ScrollArea
					className="h-full bg-background"
					style={{
						overscrollBehavior: "contain",
					}}
				>
					<FormPreview
						onAddField={onFieldAdd}
						onBlockAdd={onBlockAdd}
						onBlockDelete={onBlockDelete}
						onBlockUpdate={onBlockUpdate}
						onFieldDelete={onFieldDelete}
						onFieldSelect={onFieldSelect}
						onFieldsReorder={onFieldsReorder}
						onFormSettingsUpdate={onFormSettingsUpdate}
						onStepSelect={onStepSelect}
						schema={formSchema}
						selectedBlockId={selectedBlockId}
						selectedFieldId={selectedFieldId}
					/>
				</ScrollArea>
			</ResizablePanel>

			<ResizableHandle />

			{}
			<ResizablePanel
				defaultSize={PANEL_SIZES.RIGHT_PANEL.default}
				maxSize={PANEL_SIZES.RIGHT_PANEL.max}
				minSize={PANEL_SIZES.RIGHT_PANEL.min}
				style={{
					overscrollBehavior: "contain",
				}}
			>
				<div
					className="flex h-full flex-col"
					style={{
						overscrollBehavior: "contain",
					}}
				>
					<FieldSettingsPanel
						field={selectedField}
						onClose={() => onFieldSelect(null)}
						onFieldUpdate={onFieldUpdate}
						schema={formSchema}
					/>
					{}
				</div>
			</ResizablePanel>
		</ResizablePanelGroup>
	);
};
